import React, {useEffect, useMemo} from 'react';
import type {Asset} from '@deviceinsight/ng-ui-api-client';
import {getAssetDeviceType} from '../../ruleengine/ruleEditHelpers';
import {DIGITAL_TWIN_DATAPOINTS_BY_TYPE} from '../digitaltwin/consts';
import {CachedValueType, useMultiTelemetry, type TelemetryValue} from 'ux4iot-react';
import {usePreviousValue} from '../../../hooks/usePreviousValue';

export type AssetLiveData = Record<string, any>;
export type AssetsWithLiveData = Record<string, AssetLiveData>;

export function useAssetSubscription(
	asset: Asset | null,
	getDatapoints: (asset: Asset) => string[],
	mapper?: (liveData: AssetLiveData) => AssetLiveData
): AssetLiveData {
	const assets = useMemo(() => (asset ? [asset] : []), [asset]);
	const assetsWithLiveData = useAssetsSubscription(assets, getDatapoints, mapper);
	return React.useMemo(() => (asset ? assetsWithLiveData[asset.id] || {} : {}), [asset, assetsWithLiveData]);
}

export function useAssetsSubscription(
	assets: Asset[],
	getDatapoints: (asset: Asset) => string[],
	mapper?: (liveData: AssetLiveData) => AssetLiveData
): AssetsWithLiveData {
	const {telemetry, removeTelemetry, addTelemetry} = useMultiTelemetry({
		initialSubscribers: assets.reduce((acc, asset) => {
			acc[asset.urn] = getDatapoints(asset);
			return acc;
		}, {} as Record<string, string[]>)
	});

	const prevAssets = usePreviousValue(assets);

	useEffect(() => {
		const removedAssets = prevAssets?.filter((asset) => !assets.includes(asset));
		if (removedAssets?.length) {
			removedAssets.forEach((asset) => {
				removeTelemetry(asset.urn, getDatapoints(asset));
			});
		}
		const addedAssets = assets.filter((asset) => !prevAssets?.includes(asset));
		if (addedAssets?.length) {
			addedAssets.forEach((asset) => {
				addTelemetry(asset.urn, getDatapoints(asset));
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assets]);

	return React.useMemo(
		() =>
			Object.fromEntries(
				Object.entries(telemetry).map(([urn, values]) => {
					const asset = assets.find((asset) => asset.urn === urn);
					return [
						asset?.id,
						mapper ? mapper(mapValuesToCachedTypes(values)) : mapValuesToCachedTypes(values)
					];
				})
			),
		[assets, mapper, telemetry]
	);
}

export function getDigitalTwinDatapointsByAsset(asset: Asset) {
	const deviceType = getAssetDeviceType(asset);
	return DIGITAL_TWIN_DATAPOINTS_BY_TYPE[deviceType] || [];
}

function mapValuesToCachedTypes(values: Record<string, TelemetryValue>): Record<string, CachedValueType> {
	return Object.fromEntries(Object.entries(values).map(([datapoint, value]) => [datapoint, value.value]));
}
